import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Send, CheckCircle2, Sparkles, DollarSign, Zap, Truck, AlertTriangle, FileImage, Info } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const pricingTiers = [
  { quantity: "12 – 24 units", discount: "Base Pricing", bestFor: "Small teams or pilot launches", highlight: false },
  { quantity: "25 – 49 units", discount: "5% OFF", bestFor: "Local clubs & community events", highlight: false },
  { quantity: "50 – 99 units", discount: "10% OFF", bestFor: "Small business branding & retail", highlight: false },
  { quantity: "100 – 249 units", discount: "15% OFF", bestFor: "Corporate giveaways & conferences", highlight: true },
  { quantity: "250 – 499 units", discount: "20% OFF", bestFor: "Large scale promotions", highlight: false },
  { quantity: "500+ units", discount: "Elite Pricing", bestFor: "Contact us for a custom quote", highlight: false }
];

const whyOrderBulk = [
  {
    icon: Sparkles,
    title: "Complimentary Design Review",
    description: "Every bulk order undergoes a \"Royal Check\" to ensure your artwork is perfectly optimized for the chosen garment."
  },
  {
    icon: DollarSign,
    title: "Price Match Guarantee",
    description: "Found a better price for the same premium quality? We'll match it."
  },
  {
    icon: Zap,
    title: "Fast-Track Production",
    description: "Bulk orders over 100 units receive priority scheduling in our production queue."
  },
  {
    icon: Truck,
    title: "Flat Rate Shipping",
    description: "Enjoy simplified, low-cost shipping on all large-scale domestic orders."
  }
];

const BulkOrdersPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    quantity: '',
    productType: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">Bulk Orders</span>
          </nav>

          {/* Hero Banner */}
          <div className="relative rounded-xl overflow-hidden h-48 md:h-64 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_artisan-ink/artifacts/bhivj209_Gemini_Generated_Image_50jebd50jebd50je.png" 
              alt="Bulk Orders" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent" />
            <div className="absolute inset-0 flex items-center p-6 md:p-10">
              <div className="text-left">
                <span className="text-amber-400 font-semibold text-sm tracking-wider uppercase">
                  Corporate & Events
                </span>
                <h1 className="font-serif text-3xl md:text-4xl font-bold text-white mt-2 mb-3">
                  Bulk <span className="text-amber-400 italic">Orders</span>
                </h1>
                <p className="text-gray-300 text-lg max-w-xl">
                  Perfect for businesses, teams, events, and organizations.
                </p>
              </div>
            </div>
          </div>

          {/* Bulk Order Savings Tier */}
          <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm mb-12">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Bulk Order Savings Tier
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Maximize your budget with our tiered volume discounts. The more you print, the more you save on your total order.
              </p>
            </div>

            {/* Pricing Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Order Quantity</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Discount Level</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Best For...</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingTiers.map((tier, index) => (
                    <tr 
                      key={index} 
                      className={`border-b border-gray-100 transition-colors ${
                        tier.highlight 
                          ? 'bg-amber-50 hover:bg-amber-100' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="py-4 px-4">
                        <span className={`font-medium ${tier.highlight ? 'text-amber-700' : 'text-gray-900'}`}>
                          {tier.quantity}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                          tier.discount === 'Base Pricing' 
                            ? 'bg-gray-100 text-gray-700'
                            : tier.discount === 'Elite Pricing'
                            ? 'bg-amber-500 text-gray-900'
                            : tier.highlight
                            ? 'bg-amber-200 text-amber-800'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {tier.discount}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-gray-600">
                        {tier.bestFor}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Why Order Bulk */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
              Why Order Bulk with A&D Royal Inks?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {whyOrderBulk.map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm flex gap-4">
                  <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-500 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* File Quality Notice */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 md:p-8 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileImage className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-xl font-semibold text-gray-900 mb-3">
                  Artwork File Requirements
                </h3>
                <p className="text-gray-600 mb-4">
                  For the best print quality, please upload high-resolution files. We recommend:
                </p>
                <ul className="space-y-2 text-gray-600 mb-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>PNG or PDF files</strong> at 300 DPI or higher</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Vector files</strong> (AI, EPS, SVG) for logos and text</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Transparent backgrounds</strong> when applicable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Minimum dimensions:</strong> 2000 x 2000 pixels for detailed prints</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Warning Notice */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-6 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-800 mb-2">Important Notice About Image Quality</h4>
                <p className="text-amber-700">
                  <strong>If you upload a blurry or low-resolution file, you will receive a blurry image on your garment.</strong> We print exactly what you provide, so please ensure your artwork meets our quality standards before submitting.
                </p>
                <div className="mt-4 p-4 bg-white/60 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Info className="w-5 h-5" />
                    <span className="font-medium">Need help with your artwork?</span>
                  </div>
                  <p className="text-amber-700 mt-2 text-sm">
                    Image creation, redesign, and resolution enhancement services are available at an additional cost. Contact us for a quote on artwork services.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm">
              {!submitted ? (
                <>
                  <h2 className="font-serif text-2xl font-semibold text-gray-900 mb-6 text-center">
                    Request a Quote
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Company</label>
                        <Input
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Estimated Quantity *</label>
                        <Input
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleChange}
                          placeholder="e.g., 50-100"
                          required
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Type *</label>
                        <Input
                          name="productType"
                          value={formData.productType}
                          onChange={handleChange}
                          placeholder="e.g., T-shirts, Hoodies"
                          required
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Tell us about your project..."
                        className="w-full"
                      />
                    </div>
                    <Button 
                      type="submit"
                      className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 py-6 font-semibold rounded-lg"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Submit Request
                    </Button>
                  </form>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="font-serif text-2xl font-semibold text-gray-900 mb-2">
                    Request Submitted!
                  </h2>
                  <p className="text-gray-500 mb-6">
                    We'll get back to you within 24-48 hours with a custom quote.
                  </p>
                  <Link to="/shop">
                    <Button variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white">
                      Continue Shopping
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BulkOrdersPage;
